import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

export function useNavSettings() {
  const { user } = useAuth();
  return useQuery({
    queryKey: ["nav-settings", user?.id],
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from("nav_settings")
        .select("*")
        .limit(1)
        .maybeSingle();

      if (error) return { hidden_items: [] as string[] };
      return data as { id: string; nursery_id: string; hidden_items: string[] };
    },
    enabled: !!user,
    staleTime: 300_000,
  });
}

export function useUpdateNavSettings() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (hiddenItems: string[]) => {
      // Get nursery_id
      const { data: profile } = await supabase
        .from("profiles")
        .select("nursery_id")
        .single();
      if (!profile?.nursery_id) throw new Error("No nursery");

      // Upsert
      const { error } = await (supabase as any)
        .from("nav_settings")
        .upsert(
          { nursery_id: profile.nursery_id, hidden_items: hiddenItems },
          { onConflict: "nursery_id" }
        );
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["nav-settings"] });
    },
  });
}

// Items that can never be hidden
export const UNHIDEABLE_ITEMS = [
  "Dashboard",
  "Settings",
  "Help",
  "Audit Log",
  "My Rota",
  "Security & Trust",
  "Approvals",
  "Access",
  "Ofsted Readiness",
];

// Preset configurations
export const NAV_PRESETS = {
  core: {
    label: "Core Operations",
    description: "Best for getting started. Shows: Dashboard, Children, Staff, Rota, Register, Room Register, My Rota, Alerts. Hides everything else until you are ready.",
    visible: [
      "Dashboard", "Children", "Staff", "Rota", "Register", "Room Register", "My Rota", "Alerts",
      // Unhideable items always visible
      "Settings", "Help", "Audit Log", "Security & Trust", "Approvals", "Access", "Ofsted Readiness",
    ],
  },
  full: {
    label: "Full Platform",
    description: "Shows everything. All sections enabled.",
    visible: [] as string[], // empty = nothing hidden
  },
};
